import { useParams, useNavigate, Link } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle, LogIn } from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { useInviteInfo, useAcceptInvite } from '@/hooks/use-projects';
import { useToast } from '@/hooks/use-toast';
import { useI18n } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import type { ProjectRole } from '@/lib/schemas';

const roleLabels: Record<ProjectRole, string> = {
    OWNER: 'Owner',
    ADMIN: 'Admin',
    MEMBER: 'Member',
    VIEWER: 'Viewer',
};

export function InvitePage() {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const { toast } = useToast();
    const { t } = useI18n();
    const { isAuthenticated, user } = useAuthStore();

    const { data: inviteInfo, isLoading, error } = useInviteInfo(token || '');
    const acceptInvite = useAcceptInvite();

    const handleAccept = async () => {
        if (!token) return;

        try {
            const result = await acceptInvite.mutateAsync(token);
            toast({
                title: t('common.success'),
                description: t('projects.inviteSent'),
            });
            navigate(`/projects/${result.projectId}`);
        } catch (error) {
            toast({
                title: t('common.error'),
                description:
                    error instanceof Error ? error.message : 'Failed to accept invitation',
                variant: 'destructive',
            });
        }
    };

    if (isLoading) {
        return (
            <div className="flex min-h-screen items-center justify-center p-4">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    if (error || !inviteInfo) {
        return (
            <div className="flex min-h-screen items-center justify-center p-4">
                <Card className="max-w-md w-full p-6 sm:p-8 text-center">
                    <div className="h-14 w-14 sm:h-16 sm:w-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
                        <XCircle className="h-7 sm:h-8 w-7 sm:w-8 text-destructive" />
                    </div>
                    <h1 className="text-lg sm:text-xl font-semibold text-foreground mb-2">
                        Invalid or expired invitation
                    </h1>
                    <p className="text-sm sm:text-base text-foreground-muted mb-6">
                        This invitation link is no longer valid. It may have expired or been
                        cancelled. Please ask the project owner for a new invitation.
                    </p>
                    <Button asChild className="w-full sm:w-auto">
                        <Link to="/">{t('nav.home')}</Link>
                    </Button>
                </Card>
            </div>
        );
    }

    // If not authenticated, show login prompt
    if (!isAuthenticated) {
        return (
            <div className="flex min-h-screen items-center justify-center p-4">
                <Card className="max-w-md w-full p-6 sm:p-8">
                    <div className="text-center mb-6">
                        {inviteInfo.project.imageUrl ? (
                            <img
                                src={inviteInfo.project.imageUrl}
                                alt={inviteInfo.project.name}
                                className="h-14 w-14 sm:h-16 sm:w-16 rounded-xl mx-auto mb-4 object-cover"
                            />
                        ) : (
                            <div
                                className="h-14 w-14 sm:h-16 sm:w-16 rounded-xl flex items-center justify-center text-2xl sm:text-3xl mx-auto mb-4"
                                style={{ backgroundColor: inviteInfo.project.color || '#6366f1' }}
                            >
                                {inviteInfo.project.iconEmoji || '📁'}
                            </div>
                        )}
                        <h1 className="text-lg sm:text-xl font-semibold text-foreground mb-2">
                            You're invited to join
                        </h1>
                        <p className="text-xl sm:text-2xl font-bold text-foreground">
                            {inviteInfo.project.name}
                        </p>
                    </div>

                    <div className="bg-surface-hover rounded-lg p-3 sm:p-4 mb-6">
                        <p className="text-sm text-foreground-muted">
                            You've been invited as a <strong>{roleLabels[inviteInfo.role]}</strong>.
                            Sign in or create an account to accept this invitation.
                        </p>
                    </div>

                    <div className="space-y-3">
                        <Button asChild className="w-full">
                            <Link to={`/login?redirect=/invite/${token}`}>
                                <LogIn className="h-4 w-4 mr-2" />
                                {t('auth.signIn')}
                            </Link>
                        </Button>
                        <Button variant="outline" asChild className="w-full">
                            <Link to={`/register?redirect=/invite/${token}`}>
                                {t('auth.createAccount')}
                            </Link>
                        </Button>
                    </div>

                    <p className="text-xs text-foreground-muted text-center mt-4">
                        This invitation is for {inviteInfo.email} and expires on{' '}
                        {new Date(inviteInfo.expiresAt).toLocaleDateString()}
                    </p>
                </Card>
            </div>
        );
    }

    // Check if email matches
    const emailMatches = user?.email.toLowerCase() === inviteInfo.email.toLowerCase();

    return (
        <div className="flex min-h-screen items-center justify-center p-4">
            <Card className="max-w-md w-full p-6 sm:p-8">
                <div className="text-center mb-6">
                    {inviteInfo.project.imageUrl ? (
                        <img
                            src={inviteInfo.project.imageUrl}
                            alt={inviteInfo.project.name}
                            className="h-14 w-14 sm:h-16 sm:w-16 rounded-xl mx-auto mb-4 object-cover"
                        />
                    ) : (
                        <div
                            className="h-14 w-14 sm:h-16 sm:w-16 rounded-xl flex items-center justify-center text-2xl sm:text-3xl mx-auto mb-4"
                            style={{ backgroundColor: inviteInfo.project.color || '#6366f1' }}
                        >
                            {inviteInfo.project.iconEmoji || '📁'}
                        </div>
                    )}
                    <h1 className="text-lg sm:text-xl font-semibold text-foreground mb-2">
                        You're invited to join
                    </h1>
                    <p className="text-xl sm:text-2xl font-bold text-foreground">
                        {inviteInfo.project.name}
                    </p>
                </div>

                <div className="bg-surface-hover rounded-lg p-3 sm:p-4 mb-6">
                    <div className="flex items-center justify-between">
                        <span className="text-sm text-foreground-muted">Your role</span>
                        <span className="text-sm font-medium text-foreground">
                            {roleLabels[inviteInfo.role]}
                        </span>
                    </div>
                </div>

                {!emailMatches && (
                    <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 sm:p-4 mb-6">
                        <p className="text-sm text-amber-400">
                            <strong>Note:</strong> This invitation was sent to{' '}
                            {inviteInfo.email}, but you're signed in as {user?.email}. You'll
                            need to sign in with the correct email to accept.
                        </p>
                    </div>
                )}

                <div className="space-y-3">
                    {emailMatches ? (
                        <Button
                            onClick={handleAccept}
                            disabled={acceptInvite.isPending}
                            className="w-full"
                        >
                            {acceptInvite.isPending ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                                <CheckCircle2 className="h-4 w-4 mr-2" />
                            )}
                            Accept invitation
                        </Button>
                    ) : (
                        <Button asChild variant="outline" className="w-full">
                            <Link to={`/login?redirect=/invite/${token}`}>
                                {t('auth.signIn')} with {inviteInfo.email}
                            </Link>
                        </Button>
                    )}
                    <Button variant="ghost" asChild className="w-full">
                        <Link to="/dashboard">{t('nav.dashboard')}</Link>
                    </Button>
                </div>

                <p className="text-xs text-foreground-muted text-center mt-4">
                    This invitation expires on{' '}
                    {new Date(inviteInfo.expiresAt).toLocaleDateString()}
                </p>
            </Card>
        </div>
    );
}
